import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetAuthorWithHeadlines } from '@root/modules/author/types/author';
import getAuthorWithHeadlines from '@root/modules/author/graphql/getAuthorWithHeadlines.graphql';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = GetAuthorWithHeadlines['variables'];
type Response = GetAuthorWithHeadlines['data'];
type FetchAuthorReturn = Response['authors']['data'][0];

export default class AuthorWithHeadlinesService extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private fixVariables(vars: ServiceVariables) {
    const variables: ServiceVariables = {
      ...vars,
    };
    return variables;
  }

  private async fetchAuthorsWithHeadlines(variables: ServiceVariables): Promise<FetchAuthorReturn | undefined> {
    try {
      const options = Object.assign({ query: getAuthorWithHeadlines }, { variables });
      const dataLoaderOptions: RedisDataLoaderOptions = {
        remote: {
          keyPrefix: 'author',
        },
      };
      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      if (variables.offset !== 0) {
        dataLoaderOptions.remote!.gracePeriodMs = 600000; // 10 minutes for pager
      }

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.authors?.data.length) {
        this.handleError({ message: 'author.error.not_found', statusCode: 404 });
      }

      return response.authors.data[0];
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchAuthorsWithHeadlines(variables);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables) {
    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchAuthorsWithHeadlines(fixedVariables);

    return response;
  }
}
